'use client'

import { useEffect, useRef, useState } from 'react'
import { legacyHookAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import type { EditorMode } from '@/msgdeskRuntime/contracts/editorActions'
import { dispatchEditorAction } from '@/msgdeskRuntime/editorActions'
import { EditorBodyFields } from '@/deskUiSkeletons/editor/EditorBodyFields'
import { contract } from '@/deskUiSkeletons/editor/Editor.helpers'
import { EditorHeadFields, HeadShort } from '@/deskUiSkeletons/editor/components/Head/Head'
import { EditorConfigureModal } from '@/deskUiSkeletons/editor/components/Modal/ConfigureModal'
import { EditorAttachmentsModal, editorAttachmentCount } from '@/deskUiSkeletons/editor/components/Modal/AttachmentsModal'
import { EditorAttachmentsStrip } from '@/deskUiSkeletons/editor/components/Attachments/AttachmentsStrip'
import { useEditorAttachments } from '@/deskUiSkeletons/editor/useEditorAttachments'
import { EditorFormDefaults, HiddenEditorFormFields, useEditorTemplateContract } from '@/deskUiSkeletons/editor/EditorTemplateContract'
import { productDisplayComboValue } from '../../../productDisplayCatalog'

const productTypeValue = productDisplayComboValue('gmail')

function HiddenAction({ name, label }: { name: Parameters<typeof contract>[0]; label: string }) {
  return <button type="button" {...contract(name)} className="hidden" hidden aria-hidden="true" tabIndex={-1}>{label}</button>
}

// Configure and Attachments ride the shared unified modals; the former
// inline form/settings swap is retired.
export function GmailEditorTemplate({
  active,
  editorMode,
  settingsOpen,
  onCloseSettings,
}: {
  active: boolean
  editorMode?: EditorMode
  settingsOpen: boolean
  onCloseSettings: () => void
}) {
  const formModel = useEditorTemplateContract(editorMode)
  const [attachmentsOpen, setAttachmentsOpen] = useState(false)
  const attachCount = editorAttachmentCount(useEditorAttachments(), editorMode)
  const productTypeApplied = useRef('')

  useEffect(() => {
    if (!active || (editorMode !== 'new_msg' && editorMode !== 'new_submsg')) {
      productTypeApplied.current = ''
      return
    }
    const key = `${editorMode}:${productTypeValue}`
    if (productTypeApplied.current === key || !formModel.rough) return
    const enabled = dispatchEditorAction({
      action: 'set-more-setting',
      name: 'is_view',
      value: true,
      reason: 'gmail-surface:product-type',
    })
    const selected = dispatchEditorAction({
      action: 'set-more-setting',
      name: 'is_view_combo',
      value: productTypeValue,
      reason: 'gmail-surface:product-type',
    })
    if (enabled && selected) productTypeApplied.current = key
  }, [active, editorMode, formModel.rough])

  return (
    <div
      {...legacyHookAttrs('editor.root', `md-gmail-editor min-h-[calc(100vh-5rem)] overflow-hidden rounded-2xl border border-slate-200 bg-white text-slate-900 shadow-sm${active ? '' : ' hidden'}`)}
      style={{ display: active ? 'flex' : 'none' }}
      aria-hidden={!active}
    >
      {/* EDITOR-PAGE-PADDING: page-like breathing room inside the card. */}
      <div className="flex min-h-0 w-full flex-col p-4 sm:p-6">
        <EditorFormDefaults {...formModel} editorMode={editorMode} />
        <HeadShort />
        <button type="button" {...contract('editor.close')} className="hidden" hidden aria-hidden="true" tabIndex={-1}>Close</button>
        <form {...contract('editor.form')} className="flex min-h-0 flex-1 flex-col">
          <EditorHeadFields editorMode={editorMode} />
          <div className="flex flex-wrap items-center gap-3 px-4 py-3">
            <EditorAttachmentsStrip editorMode={editorMode} />
            <button
              type="button"
              className="inline-flex h-9 cursor-pointer items-center gap-2 rounded-md border border-[#c8d2dc] bg-white px-3 text-[13px] text-[#314253] hover:bg-[#edf2f6]"
              onClick={() => setAttachmentsOpen(true)}
            >
              Attachments{attachCount > 0 ? ` (${attachCount})` : ''}
            </button>
          </div>
          <EditorBodyFields />
          <HiddenEditorFormFields inputs={formModel.inputs} />
        </form>
        <div {...contract('editor.settings')} className="!hidden" hidden aria-hidden="true" />
        <div {...legacyHookAttrs('editor.actions', 'hidden')} hidden aria-hidden="true">
          <HiddenAction name="editor.action.save" label="Save" />
          <HiddenAction name="editor.action.draft" label="Draft" />
          <HiddenAction name="editor.action.delete" label="Delete" />
          <HiddenAction name="editor.action.clear" label="Clear" />
          <HiddenAction name="editor.action.saveAndGo" label="Save and go" />
          <HiddenAction name="editor.action.restore" label="Restore" />
        </div>
        <EditorConfigureModal open={settingsOpen} onClose={onCloseSettings} />
        <EditorAttachmentsModal open={attachmentsOpen} onClose={() => setAttachmentsOpen(false)} editorMode={editorMode} />
      </div>
    </div>
  )
}
